import { STORAGE_KEYS } from "./storageKeys";
import { callbackLogger } from "./logger";
import { safeTrack } from "../observability/tracking/safeTrack";
import type { FacebookLoginResponse } from "../types/facebook";

const INCOMPLETE_SIGNUP_ERROR =
  "WhatsApp Business Account signup incomplete. Please select your WhatsApp Business Account and enter a valid verified phone number, then try again.";

interface SignupIds {
  phoneNumberId: string | null;
  wabaId: string | null;
  businessId: string | null;
}

interface SignupOutcome {
  has_phone_number_id: boolean;
  has_waba_id: boolean;
  has_business_id: boolean;
  ids_count: number;
  is_complete: boolean;
  is_partial: boolean;
  is_empty: boolean;
}

function redirectToUrl(url: URL) {
  window.location.href = url.toString();
}

function redirectWithError(
  callbackUrl: string,
  error: string,
  originalState: string | null,
  errorReason?: string,
): void {
  const errorUrl = new URL(callbackUrl);
  errorUrl.searchParams.set("error", error);
  if (errorReason) {
    errorUrl.searchParams.set("error_reason", errorReason);
  }
  if (originalState) {
    errorUrl.searchParams.set("state", originalState);
  }
  redirectToUrl(errorUrl);
}

export function readSignupIdsFromSession(): SignupIds {
  const ids: SignupIds = {
    phoneNumberId: null,
    wabaId: null,
    businessId: null,
  };

  try {
    if (typeof window !== "undefined") {
      ids.phoneNumberId = window.sessionStorage.getItem(
        STORAGE_KEYS.phoneNumberId,
      );
      ids.wabaId = window.sessionStorage.getItem(STORAGE_KEYS.wabaId);
      ids.businessId = window.sessionStorage.getItem(STORAGE_KEYS.businessId);
    }
  } catch (error) {
    callbackLogger.error("failed_to_retrieve_wa_ids_from_session", {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  return ids;
}

export function buildSignupOutcome({
  phoneNumberId,
  wabaId,
  businessId,
}: SignupIds): SignupOutcome {
  const ids = [phoneNumberId, wabaId, businessId];
  const isComplete = ids.every(Boolean);

  return {
    has_phone_number_id: !!phoneNumberId,
    has_waba_id: !!wabaId,
    has_business_id: !!businessId,
    ids_count: ids.filter(Boolean).length,
    is_complete: isComplete,
    is_partial: ids.some(Boolean) && !isComplete,
    is_empty: ids.every((id) => !id),
  };
}

const IDS_POLL_INTERVAL_MS = 100;
const IDS_POLL_MAX_WAIT_MS = 3000;

function waitForSignupIds(): Promise<SignupIds> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      const ids = readSignupIdsFromSession();
      if (
        (ids.phoneNumberId && ids.wabaId && ids.businessId) ||
        Date.now() - start >= IDS_POLL_MAX_WAIT_MS
      ) {
        resolve(ids);
        return;
      }
      setTimeout(check, IDS_POLL_INTERVAL_MS);
    };
    check();
  });
}

function completeRedirect(
  code: string,
  callbackUrl: string,
  originalState: string | null,
  ids: SignupIds,
  actualFacebookRedirectUri?: string | null,
) {
  const redirectUrl = new URL(callbackUrl);
  redirectUrl.searchParams.set("code", code);
  if (originalState) {
    redirectUrl.searchParams.set("state", originalState);
  }

  const { phoneNumberId, wabaId, businessId } = ids;
  const signupOutcome = buildSignupOutcome(ids);

  const outcomeType = signupOutcome.is_complete
    ? "success"
    : signupOutcome.is_partial
      ? "partial_ids_no_phone"
      : signupOutcome.is_empty
        ? "no_ids_captured"
        : "unknown";

  callbackLogger.info("signup_completion_attempt", {
    ...signupOutcome,
    outcome: signupOutcome.is_complete ? "success" : "failure",
    failure_type: signupOutcome.is_complete ? null : outcomeType,
  });

  try {
    safeTrack("signup_completion", {
      success: signupOutcome.is_complete,
      has_phone_number_id: signupOutcome.has_phone_number_id,
      has_waba_id: signupOutcome.has_waba_id,
      has_business_id: signupOutcome.has_business_id,
      ids_count: signupOutcome.ids_count,
      failure_category: signupOutcome.is_complete ? null : outcomeType,
    });
  } catch {
    // Silently fail analytics - don't break user flow
  }

  if (!phoneNumberId || !wabaId || !businessId) {
    const missingIds = [];
    if (!phoneNumberId) missingIds.push("phone_number_id");
    if (!wabaId) missingIds.push("waba_id");
    if (!businessId) missingIds.push("business_id");

    callbackLogger.error("signup_incomplete_missing_required_ids", {
      missing_ids: missingIds,
      has_phone_number_id: !!phoneNumberId,
      has_waba_id: !!wabaId,
      has_business_id: !!businessId,
    });

    redirectWithError(
      callbackUrl,
      INCOMPLETE_SIGNUP_ERROR,
      originalState,
      "missing_required_ids",
    );
    return;
  }

  redirectUrl.searchParams.set("phone_number_id", phoneNumberId);
  redirectUrl.searchParams.set("waba_id", wabaId);
  redirectUrl.searchParams.set("business_id", businessId);

  const redirectUriForTokenExchange = actualFacebookRedirectUri || callbackUrl;
  redirectUrl.searchParams.set("redirect_uri", redirectUriForTokenExchange);

  const finalUrl = redirectUrl.toString();

  const finalParams = {
    code: redirectUrl.searchParams.get("code"),
    state: redirectUrl.searchParams.get("state"),
    redirect_uri: redirectUrl.searchParams.get("redirect_uri"),
    phone_number_id: redirectUrl.searchParams.get("phone_number_id"),
    waba_id: redirectUrl.searchParams.get("waba_id"),
    business_id: redirectUrl.searchParams.get("business_id"),
  };

  const redirectOutcome = {
    has_code: !!finalParams.code,
    has_phone_number_id: !!finalParams.phone_number_id,
    has_waba_id: !!finalParams.waba_id,
    has_business_id: !!finalParams.business_id,
    has_state: !!finalParams.state,
    has_redirect_uri: !!finalParams.redirect_uri,
    will_succeed:
      !!finalParams.code &&
      !!finalParams.phone_number_id &&
      !!finalParams.waba_id &&
      !!finalParams.business_id,
    url_length: finalUrl.length,
  };

  callbackLogger.info("redirecting_to_zapier_oauth_callback", {
    ...redirectOutcome,
    expected_backend_result: redirectOutcome.will_succeed
      ? "success"
      : "failure",
    redirect_uri_source: actualFacebookRedirectUri
      ? "provided_redirect_uri"
      : "cli_callback_url",
  });

  try {
    safeTrack("redirect_to_zapier", {
      has_required_params: redirectOutcome.will_succeed,
      has_code: redirectOutcome.has_code,
      has_phone_number_id: redirectOutcome.has_phone_number_id,
      has_waba_id: redirectOutcome.has_waba_id,
      expected_outcome: redirectOutcome.will_succeed
        ? "connection_success"
        : "backend_will_fail",
    });
  } catch {
    // Silently fail analytics - don't break user flow
  }

  window.location.href = finalUrl;
}

export const fbLoginCallback = (
  response: FacebookLoginResponse,
  callbackUrl: string | null,
  originalState: string | null,
  actualFacebookRedirectUri?: string | null,
) => {
  callbackLogger.info("fb_login_callback_invoked", {
    has_response: !!response,
    has_callbackUrl: !!callbackUrl,
    callbackUrl_preview: callbackUrl
      ? `${callbackUrl.substring(0, 50)}...`
      : null,
    has_originalState: !!originalState,
    has_actualFacebookRedirectUri: !!actualFacebookRedirectUri,
    actualFacebookRedirectUri_preview: actualFacebookRedirectUri
      ? `${actualFacebookRedirectUri.substring(0, 50)}...`
      : null,
    response_status: response?.status,
    has_authResponse: !!response?.authResponse,
    has_code: !!response?.authResponse?.code,
  });

  if (!callbackUrl) {
    callbackLogger.error("no_callback_url_provided", {
      message: "Cannot redirect back to Zapier",
    });
    return;
  }

  const authResponse = response.authResponse ?? {};
  const { code } = authResponse;

  if (!code) {
    if (response?.status && response.status !== "connected") {
      const statusErrors: Record<string, string> = {
        unknown: "User not logged into Facebook",
        not_authorized: "User denied app authorization",
      };
      const error =
        statusErrors[response.status] ||
        `Unexpected status: ${response.status}`;
      redirectWithError(callbackUrl, error, originalState);
      return;
    }

    const error = "No authorization code provided by Facebook";
    redirectWithError(callbackUrl, error, originalState);
    return;
  }

  // The WA_EMBEDDED_SIGNUP postMessage events that store WhatsApp IDs to
  // sessionStorage may not have been processed yet when this callback fires.
  // Poll briefly to let the event handler persist the IDs before redirecting.
  const immediateIds = readSignupIdsFromSession();
  if (
    immediateIds.phoneNumberId &&
    immediateIds.wabaId &&
    immediateIds.businessId
  ) {
    completeRedirect(
      code,
      callbackUrl,
      originalState,
      immediateIds,
      actualFacebookRedirectUri,
    );
    return;
  }

  waitForSignupIds().then((ids) => {
    completeRedirect(
      code,
      callbackUrl,
      originalState,
      ids,
      actualFacebookRedirectUri,
    );
  });
};

export function buildRedirectUrl(
  baseUrl: string,
  code: string,
  state: string | null,
  clientId: string,
): string {
  const url = new URL(baseUrl);
  url.searchParams.set("code", code);
  url.searchParams.set("client_id", clientId);
  if (state) {
    url.searchParams.set("state", state);
  }
  return url.toString();
}

export function buildStateParameter(
  callbackUrl: string | null,
  cliSessionId: string | null,
): string | null {
  if (!callbackUrl && !cliSessionId) return null;

  try {
    const stateData = {
      redirect_uri: callbackUrl,
      cli_session: cliSessionId,
    };
    return btoa(JSON.stringify(stateData));
  } catch {
    return null;
  }
}
